import { ChangeEvent, useCallback } from 'react';
import {
  useController,
  useFormContext,
  UseControllerProps,
} from 'react-hook-form';
import { ValidationOptions } from '../../../types/form';
import { transformValidationOptions } from '../../../helpers/shared/transformValidationOptions';
import { useTranslation } from 'react-i18next';

export type RadioGroupFieldValidationOptions<TFieldValues> =
  ValidationOptions<TFieldValues>;

export interface UseRadioGroupFieldControllerProps<TFieldValues>
  extends Omit<UseControllerProps<TFieldValues>, 'control' | 'rules'> {
  rules?: RadioGroupFieldValidationOptions<TFieldValues>;
  onChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
}

export function useRadioGroupFieldController<TFieldValues>(
  props: UseRadioGroupFieldControllerProps<TFieldValues>,
) {
  const { onChange, rules, ...restControllerProps } = props;

  const { t } = useTranslation();
  const { control } = useFormContext<TFieldValues>();

  const radioFieldRules = transformValidationOptions<TFieldValues>(t, rules);
  const required = typeof radioFieldRules.required !== 'undefined';

  const controllerProps = useController<TFieldValues>({
    control,
    rules: radioFieldRules,
    ...restControllerProps,
  });
  const { field } = controllerProps;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      field.onChange(event);

      if (onChange) {
        onChange(event, event.target.value);
      }
    },
    [field, onChange],
  );

  return {
    required,
    onChangeField: handleChange,
    ...controllerProps,
  };
}
